import { IGiveaway, IGiveawayParticipant, IGiveawayWithDetails, IRulesGiveaway } from '../../../interfaces';
import privateHttpClient from '../privateHttpClient';
import GiveawayMapper from '../mappers/GiveawayMapper';
import { urlHttpGov } from '../../urls';
import GiveawayParticipantMapper from '../mappers/GiveawayParticipantMapper';
import RulesGiveawayMapper from '../mappers/RulesGiveawayMapper';

class GiveawaysService {
    private baseURL: string = `${urlHttpGov}/panel/raffle`;

    async listGiveaways({ cityId, signal }: { cityId: number, signal: AbortSignal }): Promise<Array<IGiveaway>> {

        const { data } = await privateHttpClient.get(`${this.baseURL}/county/${cityId}`, { signal });

        return data.map((giveaway: any) => GiveawayMapper.toDomain(giveaway));
    }

    async createGiveaway(giveaway: IGiveaway): Promise<IGiveaway> {
        const persistenceGiveaway = GiveawayMapper.toPersistence(giveaway);

        const { data } = await privateHttpClient.post(this.baseURL, persistenceGiveaway);

        const domainGiveaway = GiveawayMapper.toDomain(data);

        return domainGiveaway;
    }

    async getGiveawayById({ id, signal }: { id: number, signal: AbortSignal }): Promise<IGiveaway> {
        const { data } = await privateHttpClient.get(`${this.baseURL}/${id}`, { signal });
        return GiveawayMapper.toDomain(data);
    }

    async getRulesGiveawayByCountyId({ county_id, signal }: { county_id: number, signal: AbortSignal }): Promise<IRulesGiveaway> {
        const { data } = await privateHttpClient.get(`${this.baseURL}/rules/county/${county_id}`, { signal });
        return RulesGiveawayMapper.toDomain(data);
    }

    async getPreviousGiveawayByCurrent({ currentGiveawayId, cityId, signal }: { currentGiveawayId: number, cityId: number, signal: AbortSignal }): Promise<IGiveaway | null> {
        const { data } = await privateHttpClient.get(`${this.baseURL}/previous/raffle/${currentGiveawayId}/county/${cityId}`, { signal });

        if (!data) {
            return null;
        }

        return GiveawayMapper.toDomain(data);
    }

    async listGiveawayParticipantsById({ id, signal }: { id: number, signal: AbortSignal }): Promise<IGiveawayParticipant[]> {
        const { data } = await privateHttpClient.get(`${this.baseURL}/${id}/participants`, { signal });

        return data.map((giveaway: any) => GiveawayParticipantMapper.toDomain(giveaway));
    }

    async updateGiveaway({ id, giveaway }: { id: number, giveaway: IGiveaway }): Promise<IGiveaway> {
        const persistenceGiveaway = GiveawayMapper.toPersistence(giveaway);

        const { data } = await privateHttpClient.patch(`${this.baseURL}/${id}`, persistenceGiveaway);

        const domainGiveaway = GiveawayMapper.toDomain(data);

        return domainGiveaway;
    }

    deleteGiveaway(id: number) {
        return privateHttpClient.delete(`${this.baseURL}/${id}`);
    }

    async listGiveawaysWithDetails({ cityId, appUserId, signal }: { cityId: number, appUserId: number, signal: AbortSignal }): Promise<Array<IGiveawayWithDetails>> {
        const { data } = await privateHttpClient.get(`${this.baseURL}/details/county/${cityId}/app_user/${appUserId}`, { signal });

        return data.map((element: any): IGiveawayWithDetails => ({
            ...GiveawayMapper.toDomain(element.raffle),
            total_tickets: element.count_tickets,
            total_invoices_balance: element.balance_of_invoice,
            total_taxes_balance: element.balance_of_taxe,
        }));
    }

    async exportGiveawayTickets(id: number): Promise<{ hash: string, fileName: string }> {
        const { data } = await privateHttpClient.post(`${this.baseURL}/file`, { raffleId: id });

        return {
            hash: data.hash,
            fileName: data.fileName,
        };
    }

    async downloadGiveawayTicketsFile({ fileName, signal }: { fileName: string, signal: AbortSignal }): Promise<Blob> {
        const response = await privateHttpClient.get(`${this.baseURL}/files/${fileName}`, {
            responseType: 'arraybuffer',
            headers: {
                Accept: 'application/json',
            },
            signal: signal,
        });

        return new Blob([response.data], { type: 'application/json' });
    }

    async uploadGiveawayResult(formData: FormData): Promise<{ hash: string, file: string }> {
        const { data } = await privateHttpClient.post(`${this.baseURL}/generate_winners`, formData, {
            headers: { "Content-Type": 'multipart-formdata' }
        });

        const giveaway: IGiveaway = data;

        const lastAwardHistory = giveaway.raffle_history && giveaway.raffle_history[giveaway.raffle_history.length - 1];
        return {
            hash: lastAwardHistory?.hash_with_winners ?? "",
            file: lastAwardHistory?.file_with_winners ?? "",
        };
    }

    async uploadGiveawayFederalLotteryResult({ raffleId, formData }: { raffleId: number, formData: FormData }): Promise<IGiveaway> {
        const { data } = await privateHttpClient.post(`${this.baseURL}/generate-winners-federal-lottery`, formData, {
            headers: { "Content-Type": 'multipart-formdata' },
            params: { raffleId },
        });

        return GiveawayMapper.toDomain(data);
    }

    async downloadGiveawayWinnersFile({ fileName, signal }: { fileName: string, signal: AbortSignal }): Promise<Blob> {
        const response = await privateHttpClient.get(`${this.baseURL}/files/${fileName}`, {
            responseType: 'arraybuffer',
            headers: {
                Accept: 'application/json',
            },
            signal: signal,
        });

        return new Blob([response.data], { type: 'application/json' });
    }

    async getGiveawayByCountyIdAndDate({ cityId, date, signal }: { cityId: number, date: string, signal: AbortSignal }): Promise<IGiveaway> {
        const { data } = await privateHttpClient.get(`${this.baseURL}/county/${cityId}/interval?date=${date}`, { signal });

        return GiveawayMapper.toDomain(data);
    }
}

// eslint-disable-next-line import/no-anonymous-default-export
export default new GiveawaysService();
